import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Star as StarIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import veganBackground from '../../images/vegan.png';
import veganDefaultImage from '../../images/vegan-default.jpg';
import veganFoodsImage from '../../images/vegan-foods.jpeg';
import veganTableImage from '../../images/vegan-table.jpeg';

const PAGE_URL = 'https://createadish.com/dietary-guide/vegan';
const PAGE_TITLE = 'Vegan Diet Guide: Plant-Based Living - Create A Dish';
const PAGE_DESCRIPTION = 'A strictly plant-based lifestyle that excludes all animal products, including meat, dairy, eggs, and often honey.';
const PAGE_IMAGE = 'https://createadish.com/images/vegan-og.png';

const Vegan: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="vegan diet, plant-based, vegan lifestyle, animal-free, dairy-free, egg-free, ethical eating" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Vegan Diet Guide: Plant-Based Living",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": { "@type": "Organization", "name": "Create A Dish" },
            "publisher": { "@type": "Organization", "name": "Create A Dish", "logo": { "@type": "ImageObject", "url": "https://createadish.com/logo.png" } },
            "datePublished": "2025-12-24",
            "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
            "keywords": "PlantBased, Ethics, Vegan"
          })}
        </script>
      </Helmet>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ height: { xs: '40vh', md: '50vh' }, backgroundImage: `url(${veganBackground})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(1px)', position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.5)', px: 2, textAlign: 'center' }}>
            <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)', mb: 2 }}>Vegan Diet</Typography>
            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>Plant-Based Living</Typography>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ 
              mb: 4,
              '& .MuiBreadcrumbs-separator': {
                color: 'primary.main'
              }
            }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              onClick={() => navigate('/resources')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              onClick={() => navigate('/dietary-guide')}
              sx={{ 
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                color: 'text.primary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
            >
              Dietary Guide
            </Link>
            <Typography 
              sx={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: 'primary.main'
              }}
            >
              Vegan Diet
            </Typography>
          </Breadcrumbs>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', mb: 4, fontWeight: 'bold', color: 'primary.main' }}>The Vegan Diet</Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', maxWidth: '800px', mx: 'auto', lineHeight: 1.6, fontSize: { xs: '1rem', md: '1.1rem' } }}>{PAGE_DESCRIPTION}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            {['#PlantBased', '#Ethics', '#Vegan'].map(tag => <Typography key={tag} variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{tag}</Typography>)}
          </Box>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the Vegan Diet, a strictly plant-based lifestyle that excludes all animal products, including meat, dairy, eggs, and often honey. Rooted in ethics, health, and sustainability, veganism promotes compassion for animals while offering potential benefits for personal well-being and the environment.
          </Typography>
          <Box component="img" src={veganDefaultImage} alt="Vegan diet" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The vegan diet has ancient origins, with plant-based eating practiced in cultures like ancient India (Jainism and Buddhism) and Greece for ethical and health reasons. The modern term "vegan" was coined in 1944 by Donald Watson, who founded The Vegan Society in the UK to distinguish from vegetarianism by excluding all animal products.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The vegan diet focuses on whole, minimally processed plant foods, ensuring nutritional balance through variety.
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fruits and Vegetables</strong>: Berries, apples, oranges, leafy greens, broccoli, and carrots.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Whole Grains</strong>: Oats, quinoa, brown rice, and barley.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Legumes and Plant Proteins</strong>: Beans, lentils, chickpeas, tofu, tempeh.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Nuts and Seeds</strong>: Almonds, walnuts, chia, flaxseeds.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Plant-Based Alternatives</strong>: Nut milks, vegan cheeses, yogurts.</Typography>
            </Box>
            <Box component="img" src={veganFoodsImage} alt="Vegan foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              A well-planned vegan diet can lower risks of heart disease, type 2 diabetes, and certain cancers through high fiber, antioxidants, and low saturated fats. It supports weight management, improves gut health, and may enhance longevity.
            </Typography>
            <Box component="img" src={veganTableImage} alt="Vegan table" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the Vegan Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Start Gradually</strong>: Begin with vegan days or swap one meal at a time.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Ensure Nutrition</strong>: Supplement B12, focus on iron-rich foods.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Use AI</strong>: Generate recipes on CreateADish.com, like "vegan lentil curry."</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Explore Alternatives</strong>: Try plant milks, cheeses, and meats.</Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>Ready to Create Your Perfect Dish?</Typography>
            <Button variant="contained" size="large" startIcon={<StarIcon />} onClick={() => { setMode('generate'); navigate('/composer'); }} sx={{ minWidth: 200, fontSize: '1.1rem', py: 1.5, px: 4 }}>Get Started</Button>
          </Box>
          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">© 2025 Create A Dish. Made with ❤️ for food lovers everywhere.</Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Vegan;
